import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, AlertTriangle, CheckCircle, TrendingUp, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioReview = ({ user, onLogout }) => {
  const [reviews, setReviews] = useState([]);
  const [clients, setClients] = useState([]);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    portfolio_id: '',
    risk_profile_id: '',
  });
  const [portfolios, setPortfolios] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/risk-profiles`),
      ]);
      setClients(clientsRes.data);
      setRiskProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientPortfolios = async (clientId) => {
    try {
      const response = await axios.get(`${API}/portfolios/client/${clientId}`);
      setPortfolios(response.data);
    } catch (error) {
      toast.error('Failed to fetch portfolios');
    }
  };

  const handleClientChange = (clientId) => {
    setFormData({ ...formData, client_id: clientId, portfolio_id: '' });
    if (clientId) {
      fetchClientPortfolios(clientId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/portfolio-reviews`, formData);
      toast.success('Portfolio review created successfully');
      setSelectedReview(response.data);
      setDialogOpen(false);
      setFormData({ client_id: '', portfolio_id: '', risk_profile_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create review');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="portfolio-review-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Portfolio Review
            </h1>
            <p className="text-gray-600">Analyze portfolios and identify deviations</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="create-review-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Review
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Portfolio Review</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="client">Select Client *</Label>
                  <select
                    id="client"
                    value={formData.client_id}
                    onChange={(e) => handleClientChange(e.target.value)}
                    required
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="client-select"
                  >
                    <option value="">Choose a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_name} - {client.type}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.client_id && (
                  <div>
                    <Label htmlFor="portfolio">Select Portfolio *</Label>
                    <select
                      id="portfolio"
                      value={formData.portfolio_id}
                      onChange={(e) => setFormData({ ...formData, portfolio_id: e.target.value })}
                      required
                      className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                      data-testid="portfolio-select"
                    >
                      <option value="">Choose a portfolio...</option>
                      {portfolios.map((portfolio) => (
                        <option key={portfolio.id} value={portfolio.id}>
                          Portfolio - ${portfolio.total_value.toLocaleString()} (as of {new Date(portfolio.as_on_date).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <Label htmlFor="risk_profile">Risk Profile *</Label>
                  <select
                    id="risk_profile"
                    value={formData.risk_profile_id}
                    onChange={(e) => setFormData({ ...formData, risk_profile_id: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                    data-testid="risk-profile-select"
                  >
                    <option value="">Choose a risk profile...</option>
                    {riskProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-review-button">
                  Generate Review
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {selectedReview ? (
          <div className="space-y-6">
            <Card className="border-slate-700 bg-slate-900/80 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Review Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-400 text-sm mb-1">Review ID</p>
                    <p className="text-white font-semibold">{selectedReview.id.substring(0, 8)}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-400 text-sm mb-1">Status</p>
                    <p className="text-ugmonk-grey font-semibold capitalize">{selectedReview.status}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-400 text-sm mb-1">Deviations Found</p>
                    <p className="text-white font-semibold">{selectedReview.deviations.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedReview.deviations.length > 0 && (
              <Card className="border-slate-700 bg-slate-900/80 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="text-white text-2xl flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                    Portfolio Deviations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {selectedReview.deviations.map((deviation, index) => (
                      <Card key={index} className="border-slate-700 bg-slate-800/50" data-testid={`deviation-card-${index}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-white font-semibold text-lg mb-2">{deviation.product}</h4>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-slate-400">Target</p>
                                  <p className="text-ugmonk-grey font-semibold">{deviation.target.toFixed(2)}%</p>
                                </div>
                                <div>
                                  <p className="text-slate-400">Current</p>
                                  <p className="text-white font-semibold">{deviation.current.toFixed(2)}%</p>
                                </div>
                                <div>
                                  <p className="text-slate-400">Deviation</p>
                                  <div className="flex items-center gap-1">
                                    {deviation.deviation > 0 ? (
                                      <TrendingUp className="w-4 h-4 text-red-400" />
                                    ) : (
                                      <TrendingDown className="w-4 h-4 text-ugmonk-grey" />
                                    )}
                                    <p className={`font-semibold ${deviation.deviation > 0 ? 'text-red-400' : 'text-ugmonk-grey'}`}>
                                      {Math.abs(deviation.deviation).toFixed(2)}%
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedReview.ai_analysis && (
              <Card className="border-ugmonk-yellow/30 bg-gradient-to-br from-[#fffdf0] to-[#f8f6f1] backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="text-white text-2xl flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-ugmonk-grey" />
                    AI-Powered Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-6 bg-slate-900/50 rounded-lg">
                    <p className="text-white leading-relaxed whitespace-pre-wrap">{selectedReview.ai_analysis}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card className="border-slate-700 bg-slate-900/80 backdrop-blur-xl">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <h3 className="text-white text-lg font-semibold mb-2">No Review Selected</h3>
              <p className="text-slate-400 mb-4">Create a new portfolio review to get started</p>
              <Button onClick={() => setDialogOpen(true)} className="btn-primary">
                Create Review
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PortfolioReview;
